import * as _ from "lodash";
import { MapWithPathSupport } from "../../shared/utilities";
import {
    DataSource,
    SnapshotContext,
    SnapshotPosition,
    Storyline,
    StorylineState
} from "./types";
import { format } from "date-fns";

export interface SnapshotMetaV1 {
    kind: "qerent-snapshot-meta";
    version: 1;
    // Injected by the Metastore on create (mirrored from the storage path)...
    id?: string;
    owner?: string;
    name?: string;
    capturedAt: string;
    sharedWith: string[];
    // Uncompressed size of the data document (it is gzip'd on the wire and at rest)...
    sizeBytes: number;
    storylineName: string;
    // Origin-relative document location as at capture time - "Go live" returns here...
    capturedUrl: string;
    // Informational only - snapshots always live on the Metastore's default branch;
    // this records the viewer's active branch at capture time...
    branch: string;
}

export interface SnapshotDataV1 {
    kind: "qerent-snapshot-data";
    version: 1;
    // The state-model storyline is embedded, so a snapshot renders
    // identically even after the storyline (or its live template edits) changes...
    storyline: Storyline;
    datasources: Record<string, DataSource>;
    datasourceValues: Record<string, unknown>;
    parameterValues: Record<string, unknown>;
    canvasState: object;
    position: SnapshotPosition;
}

export interface SnapshotEnvelopeV1 {
    meta: SnapshotMetaV1;
    data: SnapshotDataV1;
}

export class SnapshotFormatError extends Error { }

// Templates can place arbitrary values into canvasState/parameters (callbacks, element refs);
// those are meaningless once revived from JSON, so they are dropped rather than stringified...
export function snapshotSerializer(_key: string, value: unknown): any {
    if (_.isFunction(value) || _.isElement(value) || _.isSymbol(value) || _.has(value, "$$typeof")) {
        return undefined;
    }

    return value;
}

export function buildSnapshotData(storylineState: StorylineState): SnapshotDataV1 {
    return {
        kind: "qerent-snapshot-data",
        version: 1,
        storyline: storylineState.storyline,
        datasources: Object.fromEntries(storylineState.datasources),
        datasourceValues: Object.fromEntries(storylineState.datasourceValues),
        parameterValues: _.omitBy(Object.fromEntries(storylineState.parameterValues), _.isUndefined),
        canvasState: storylineState.canvasState ?? {},
        position: {
            chapterIndex: storylineState.chapterIndex,
            pageIndex: storylineState.pageIndex,
            paragraphIndex: storylineState.paragraphIndex
        }
    };
}

export function serializeSnapshotData(data: SnapshotDataV1): string {
    return JSON.stringify(data, snapshotSerializer);
}

export function getSerializedSizeBytes(serialized: string): number {
    return new TextEncoder().encode(serialized).byteLength;
}

export function formatSnapshotTimestamp(capturedAt: string | Date): string {
    const date = new Date(capturedAt as any);
    return isNaN(date.valueOf()) ? String(capturedAt) : format(date, "yyyy-MM-dd HH:mm");
}

export function formatSnapshotSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes ?? 0} bytes`;
}

export interface SnapshotCaptureOptions {
    name?: string;
    sharedWith: string[];
    capturedUrl: string;
    branch: string;
}

export function buildSnapshotEnvelope(storylineState: StorylineState, options: SnapshotCaptureOptions): SnapshotEnvelopeV1 {
    const data = buildSnapshotData(storylineState);
    const sizeBytes = getSerializedSizeBytes(serializeSnapshotData(data));

    const meta: SnapshotMetaV1 = {
        kind: "qerent-snapshot-meta",
        version: 1,
        name: options.name || undefined,
        capturedAt: new Date().toISOString(),
        sharedWith: options.sharedWith ?? [],
        sizeBytes,
        storylineName: storylineState.storyline.name,
        capturedUrl: options.capturedUrl,
        branch: options.branch
    };

    return { meta, data };
}

export function validateSnapshotEnvelope(envelope: any): SnapshotEnvelopeV1 {
    const meta = envelope?.meta;
    const data = envelope?.data;

    if (meta?.kind !== "qerent-snapshot-meta" || data?.kind !== "qerent-snapshot-data") {
        throw new SnapshotFormatError("The requested document is not a snapshot.");
    }

    if (meta.version !== 1 || data.version !== 1) {
        throw new SnapshotFormatError(`Unsupported snapshot version (meta v${meta.version}, data v${data.version}).`);
    }

    if (!data.storyline?.chapters) {
        throw new SnapshotFormatError("The snapshot does not contain a storyline.");
    }

    return { meta, data };
}

export function snapshotContextFromMeta(meta: SnapshotMetaV1): SnapshotContext {
    return {
        snapshotId: meta.id,
        owner: meta.owner,
        name: meta.name,
        capturedAt: meta.capturedAt,
        storylineName: meta.storylineName,
        capturedUrl: meta.capturedUrl,
        branch: meta.branch
    };
}

export function rebuildSnapshotMaps(data: SnapshotDataV1) {
    return {
        datasources: new Map<string, DataSource>(Object.entries(data.datasources ?? {})),
        datasourceValues: new Map<string, unknown>(Object.entries(data.datasourceValues ?? {})),
        parameterValues: new MapWithPathSupport<unknown>(Object.entries(data.parameterValues ?? {}))
    };
}

export function buildSnapshotLink(storylineName: string, owner: string, snapshotId: string, origin: string): string {
    const url = new URL(`/storylines/${encodeURIComponent(storylineName)}`, origin);
    url.searchParams.set("_snapshot", snapshotId);
    url.searchParams.set("_snapshotOwner", owner);

    // The owner is a raw email address and "@" is legal in a query string -
    // decode URLSearchParams' form-encoding so shared links stay human-readable...
    return url.toString().replace(/%40/g, "@");
}
